import { useState, useEffect, useMemo, useCallback } from 'react';
import { C, FONT } from './adminShared';
import { getBusinessPlan, saveBusinessPlan } from '../../utils/api';
import {
  DEFAULT_RATES, SEED_OFFERS, SALES_RATES, projectYear, offerYearTotal,
  rateSummary, money,
} from './revenueShared';

// ─── THE PLANNER — PROJECTED MONEY, AND ONLY PROJECTED MONEY ─────────────────
// Deliberately a different surface from everything above it: its own tinted
// panel, its own heading, the word "if" everywhere. Real revenue is near zero
// and will stay low for a while; a projection sitting beside it looking similar
// would make the whole tab untrustworthy. NOTHING here shares a card, a row, a
// total or a chart series with an actual figure.
//
// One saved set of assumptions, never named scenarios. Decided deliberately.

const PANEL = '#FBF7EF';   // warmer than the page, so the boundary is obvious
const input = {
  fontFamily: FONT, fontSize: 12, padding: '6px 8px',
  border: `1px solid ${C.border}`, borderRadius: 6, background: '#fff', color: C.text,
  width: '100%', boxSizing: 'border-box',
};
const pill = (on) => ({
  padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, fontFamily: FONT,
  border: `1px solid ${on ? C.heading : C.border}`,
  background: on ? C.heading : C.card, color: on ? '#FFF8F7' : C.accent, cursor: 'pointer',
});

const PLANS = { upfront: 'All at once', split: 'Split over', retainer: 'Every month' };

/**
 * 12-month projection chart. Hand-rolled SVG because the admin has NO charting
 * library — adminShared.js draws its charts the same way, and adding recharts
 * for one panel would be a new dependency and a second visual language.
 */
function ProjectionChart({ rows, view, rates }) {
  const W = 320, H = 120, pad = 2;
  const maxBar = Math.max(1, ...rows.map(r => Math.max(r.inn, r.out)));
  const cums = rows.map(r => r.cum);
  const cMin = Math.min(0, ...cums), cMax = Math.max(1, ...cums);
  const slot = W / rows.length;
  const bw = Math.max(3, slot / 2 - pad);
  const y = (v) => H - (v / maxBar) * (H - 16);
  const cy = (v) => H - ((v - cMin) / (cMax - cMin || 1)) * (H - 16);
  const line = rows.map((r, i) => `${i * slot + slot / 2},${cy(r.cum)}`).join(' ');
  const zeroY = cy(0);

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 140, overflow: 'visible' }}
           role="img" aria-label="Projected money in, money out and running total over twelve months">
        <line x1="0" y1={zeroY} x2={W} y2={zeroY} stroke={C.border} strokeWidth="1" />
        {rows.map((r, i) => (
          <g key={i}>
            <rect x={i * slot + pad} y={y(r.inn)} width={bw} height={Math.max(1, H - y(r.inn))}
                  fill={C.chartGold} rx="1" />
            <rect x={i * slot + pad + bw} y={y(r.out)} width={bw} height={Math.max(1, H - y(r.out))}
                  fill={C.chartBar} rx="1" />
          </g>
        ))}
        <polyline points={line} fill="none" stroke={C.green} strokeWidth="1.8" />
      </svg>
      <div style={{ display: 'flex' }}>
        {rows.map((r, i) => (
          <span key={i} style={{ flex: 1, textAlign: 'center', fontSize: 8, color: C.accent }}>{i + 1}</span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 14, marginTop: 8, flexWrap: 'wrap' }}>
        {[['Lands in your account', C.chartGold], ['Goes out', C.chartBar], ['Running total', C.green]].map(([l, col]) => (
          <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, color: C.accent }}>
            <span style={{ width: 10, height: 10, background: col, borderRadius: 2 }} /> {l}
          </span>
        ))}
      </div>
    </div>
  );
}

export function CostPlanner({ outPerDayAud, view, rates, onRatesChange, canWrite }) {
  const [offers, setOffers]     = useState(SEED_OFFERS);
  const [spreadAll, setSpread]  = useState(true);
  const [loaded, setLoaded]     = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);
  const [savedAt, setSavedAt]   = useState(null);

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await getBusinessPlan();
        if (!live) return;
        if (data.rates) onRatesChange({ ...DEFAULT_RATES, ...data.rates });
        if (Array.isArray(data.offers) && data.offers.length) {
          // ⚠ AN OFFER SAVED BEFORE RATES EXISTED HOLDS A YEARLY COUNT. Tagging
          // it 'year' on the way in is what stops a stored 32 being re-read as
          // "32 per week" and multiplying his plan by 52 without a word.
          setOffers(data.offers.map(o => ({ ...o, rate: o.rate || 'year' })));
        }
        if (typeof data.spreadAll === 'boolean') setSpread(data.spreadAll);
      } catch (e) {
        if (live) setError(e.response?.data?.error || e.message);
      } finally {
        if (live) setLoaded(true);
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = useCallback(async (patch) => {
    if (!canWrite) return;
    setSaving(true); setError(null);
    try {
      // MERGES server-side — dismissedLedgerKeys and costGroups share this row.
      await saveBusinessPlan(patch);
      setSavedAt(new Date());
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  }, [canWrite]);

  const rows = useMemo(
    () => projectYear(offers, outPerDayAud, spreadAll, rates),
    [offers, outPerDayAud, spreadAll, rates],
  );
  const totalIn  = rows.reduce((n, r) => n + r.inn, 0);
  const totalOut = rows.reduce((n, r) => n + r.out, 0);
  const net      = totalIn - totalOut;
  const breakEven = rows.findIndex(r => r.cum > 0);

  const upd = (id, patch) => setOffers(os => os.map(o => (o.id === id ? { ...o, ...patch } : o)));
  const del = (id) => setOffers(os => os.filter(o => o.id !== id));
  const add = () => setOffers(os => [...os, {
    id: `o${Date.now()}`, name: 'New offer', count: 1, rate: 'month', price: 2400,
    currency: 'USD', plan: 'split', months: 4,
  }]);

  const Out = ({ label, value, sub, tone }) => (
    <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10,
      padding: '12px 14px', flex: 1, minWidth: 140 }}>
      <div style={{ fontSize: 10, color: C.accent, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, lineHeight: 1.15, marginTop: 6,
        color: tone === 'out' ? C.red : tone === 'in' ? C.green : C.heading }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: C.accent, marginTop: 4 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ marginTop: 30, background: PANEL, border: `1.5px dashed ${C.accent}`,
      borderRadius: 14, padding: '18px 14px 22px' }}>

      {/* The boundary between real and imagined, stated rather than implied. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
          color: '#FFF8F7', background: C.accent, borderRadius: 20, padding: '3px 10px' }}>Projection</span>
        <h3 style={{ margin: 0, fontFamily: "'Forum', serif", fontSize: '1.15rem', color: C.heading, letterSpacing: '0.03em' }}>
          What if
        </h3>
      </div>
      <div style={{ fontSize: 11.5, color: C.accent, margin: '6px 0 16px', maxWidth: 620 }}>
        None of this is real money. Nothing below is counted anywhere above, and no figure here is
        mixed with actual revenue.
      </div>

      {error && (
        <div style={{ background: C.redBg, border: '1px solid #F0C4BC', borderRadius: 8,
          padding: '8px 10px', fontSize: 11.5, color: C.red, marginBottom: 12 }}>{error}</div>
      )}

      {/* ── EXCHANGE RATES ──────────────────────────────────────────────────── */}
      <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 14px' }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: C.heading }}>Exchange rates</div>
        <div style={{ fontSize: 11, color: C.accent, margin: '4px 0 12px' }}>
          Money leaving and money arriving convert differently, so they are set separately. No live lookup — nudge them when they drift.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          {[
            ['outUsdAud',    'A US$1 bill costs you',   '0.01'],
            ['outGbpAud',    'A £1 bill costs you',     '0.01'],
            ['foreignFeePct', "Bank's foreign fee (%)", '0.1'],
            ['inUsdAud',     'Charge US$1, this lands', '0.01'],
          ].map(([k, label, step]) => (
            <label key={k} style={{ fontSize: 11, color: C.text }}>
              {label}
              <input type="number" step={step} value={rates[k]} disabled={!canWrite}
                onChange={e => onRatesChange({ ...rates, [k]: Number(e.target.value) || 0 })}
                onBlur={() => save({ rates })}
                style={{ ...input, marginTop: 4, borderColor: k === 'inUsdAud' ? C.green : C.border }} />
            </label>
          ))}
        </div>
        {/* Stated because deducting Stripe's cut twice is the easy mistake here. */}
        <div style={{ fontSize: 10.5, color: C.accent, marginTop: 10 }}>
          "Charge US$1, this lands" already has Stripe's cut taken out — from a real payment, US$75 charged
          arriving as A$97.87. Nothing else is deducted anywhere.
        </div>
      </div>

      {/* ── OFFERS ──────────────────────────────────────────────────────────── */}
      <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 14px', marginTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: C.heading }}>If you sell this much</div>
        <div style={{ fontSize: 11, color: C.accent, margin: '4px 0 12px' }}>
          Prices are what you <strong>charge</strong>. The year total is what actually lands, after conversion.
        </div>

        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 11.5, color: C.text,
          background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', marginBottom: 14 }}>
          <input type="checkbox" checked={spreadAll} disabled={!canWrite}
            onChange={e => { setSpread(e.target.checked); save({ spreadAll: e.target.checked }); }}
            style={{ width: 16, height: 16, accentColor: C.heading }} />
          Sales arrive steadily through the year
          <span style={{ color: C.accent, fontSize: 10.5 }}>
            {spreadAll ? '— spread evenly' : '— everyone signs in month 1'}
          </span>
        </label>

        {/* Stacked cards, not a table — eight columns cannot fit 375px. */}
        {offers.map(o => (
          <div key={o.id} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 10px', marginBottom: 8 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <input value={o.name} disabled={!canWrite} onChange={e => upd(o.id, { name: e.target.value })}
                onBlur={() => save({ offers })} style={{ ...input, fontWeight: 600 }} />
              {canWrite && (
                <button onClick={() => { del(o.id); }} title="Remove this row"
                  style={{ background: 'none', border: 'none', color: C.accent, fontSize: 17, cursor: 'pointer', lineHeight: 1, padding: '2px 0' }}>×</button>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(84px, 1fr))', gap: 8, marginTop: 8 }}>
              <label style={{ fontSize: 10, color: C.accent }}>How many
                <input type="number" min="0" step="0.5" value={o.count} disabled={!canWrite}
                  onChange={e => upd(o.id, { count: Number(e.target.value) || 0 })} onBlur={() => save({ offers })}
                  style={{ ...input, marginTop: 3 }} /></label>
              <label style={{ fontSize: 10, color: C.accent }}>How often
                <select value={o.rate || 'year'} disabled={!canWrite}
                  onChange={e => { upd(o.id, { rate: e.target.value }); }} onBlur={() => save({ offers })}
                  style={{ ...input, marginTop: 3 }}>
                  {Object.entries(SALES_RATES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select></label>
              <label style={{ fontSize: 10, color: C.accent }}>You charge
                <input type="number" value={o.price} disabled={!canWrite}
                  onChange={e => upd(o.id, { price: Number(e.target.value) || 0 })} onBlur={() => save({ offers })}
                  style={{ ...input, marginTop: 3 }} /></label>
              <label style={{ fontSize: 10, color: C.accent }}>In
                <select value={o.currency} disabled={!canWrite}
                  onChange={e => { upd(o.id, { currency: e.target.value }); }} onBlur={() => save({ offers })}
                  style={{ ...input, marginTop: 3 }}>
                  <option>USD</option><option>AUD</option>
                </select></label>
              <label style={{ fontSize: 10, color: C.accent }}>How they pay
                <select value={o.plan} disabled={!canWrite}
                  onChange={e => upd(o.id, { plan: e.target.value })} onBlur={() => save({ offers })}
                  style={{ ...input, marginTop: 3 }}>
                  {Object.entries(PLANS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select></label>
              <label style={{ fontSize: 10, color: C.accent }}>Over months
                <input type="number" value={o.months} disabled={!canWrite || o.plan === 'upfront'}
                  onChange={e => upd(o.id, { months: Number(e.target.value) || 1 })} onBlur={() => save({ offers })}
                  style={{ ...input, marginTop: 3, opacity: o.plan === 'upfront' ? 0.4 : 1 }} /></label>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10,
              flexWrap: 'wrap', marginTop: 8 }}>
              {/* His unit on the left, the annual picture on the right. */}
              <span style={{ fontSize: 11, color: C.accent }}>{rateSummary(o)}</span>
              <strong style={{ fontSize: 11.5, color: C.green }}>
                lands {money(offerYearTotal(o, rates), view, rates)} in a year
              </strong>
            </div>
          </div>
        ))}
        {canWrite && (
          <button onClick={add} style={{ ...pill(false), marginTop: 6 }}>Add an offer</button>
        )}
        {loaded && (
          <div style={{ fontSize: 10, color: C.accent, marginTop: 10 }}>
            {saving ? 'Saving…' : savedAt ? `Saved ${savedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}` : 'Changes save when you leave a field.'}
          </div>
        )}
      </div>

      {/* ── OUTPUTS ─────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 14 }}>
        <Out label="Would land in 12 months" value={money(totalIn, view, rates)} tone="in" sub="after conversion" />
        <Out label="Would go out, 12 months" value={money(totalOut, view, rates)} tone="out" sub="ads plus running costs" />
        <Out label="Would be left" value={money(net, view, rates)} tone={net >= 0 ? 'in' : 'out'}
             sub={net >= 0 ? 'after everything' : 'short by this much'} />
        <Out label="In the black from" value={breakEven === -1 ? 'Not this year' : `Month ${breakEven + 1}`}
             sub="running total turns positive" />
      </div>

      <div style={{ background: '#fff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 14px', marginTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 800, color: C.heading, marginBottom: 10 }}>The year ahead, if this holds</div>
        <ProjectionChart rows={rows} view={view} rates={rates} />
      </div>
    </div>
  );
}
